<template>
    <div>
        <b-modal id="modal-navigation" :title="editing ? 'Navigasyon Güncelle' : 'Yeni Navigasyon Ekle'" size="xl" no-close-on-backdrop scrollable>
            <navigation-form
                v-model="editedItem"
                :errors="errors"
                :languages="languages"
                :contents="formattedContents"
                :parent-navigations="formattedParentNavigations"
                :current-navigation-type="selectedNavigationType"
            />
            <template #modal-footer>
                <v-executer :loading="loading" :action="save" :text="editing ? 'Güncelle' : 'Oluştur'" />
            </template>
        </b-modal>

        <b-card>
            <div class="navigation-header">
                <div class="nav-type-selector">
                    <button
                        v-for="navType in navigationTypes"
                        :key="navType.value"
                        :class="['nav-type-btn', { active: selectedNavigationType === navType.value }]"
                        @click="changeNavigationType(navType.value)"
                    >
                        {{ navType.label }}
                    </button>
                </div>

                <div class="navigation-controls">
                    <b-form-select v-model="selectedLanguage" :options="languages" value-field="LanguageCode" text-field="LanguageName" size="sm" class="language-select" @change="loadNavigations" />
                    <b-button variant="primary" size="sm" @click="createNew">
                        <feather-icon icon="PlusIcon" size="14" />
                        <span class="ml-50">Yeni Ekle</span>
                    </b-button>
                </div>
            </div>

            <div class="navigation-content">
                <b-overlay :show="treeLoading" rounded="sm">
                    <div v-if="navigationTree && navigationTree.length > 0" class="navigation-list">
                        <vue-nestable v-model="navigationTree" :max-depth="5" :children-prop="'children'" :key-prop="'Id'" @change="handleOrderChange">
                            <template #default="{ item }">
                                <vue-nestable-handle :item="item">
                                    <div class="nav-item">
                                        <div class="nav-item-content">
                                            <div class="nav-item-left">
                                                <feather-icon icon="MenuIcon" size="18" class="drag-handle" />
                                                <span class="nav-item-title">
                                                    {{ getTranslation(item, selectedLanguage) }}
                                                </span>
                                                <span class="nav-item-key">
                                                    {{ item.NavigationKey }}
                                                </span>
                                            </div>
                                            <div class="nav-item-right">
                                                <span :class="['nav-item-status', item.IsActive ? 'active' : 'inactive']">
                                                    {{ item.IsActive ? "Aktif" : "Pasif" }}
                                                </span>
                                                <button class="nav-action-btn edit" @click="edit(item)">
                                                    <feather-icon icon="Edit2Icon" size="16" />
                                                </button>
                                                <button class="nav-action-btn delete" @click="deleteItem(item)">
                                                    <feather-icon icon="TrashIcon" size="16" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </vue-nestable-handle>
                            </template>
                        </vue-nestable>
                    </div>

                    <div v-else class="empty-state">
                        <feather-icon icon="MenuIcon" size="48" class="empty-icon" />
                        <p class="empty-text">Henüz navigasyon öğesi eklenmedi</p>
                        <b-button variant="primary" size="sm" @click="createNew">
                            <feather-icon icon="PlusIcon" size="14" />
                            <span class="ml-50">İlk Navigasyonu Ekle</span>
                        </b-button>
                    </div>
                </b-overlay>
            </div>
        </b-card>
    </div>
</template>

<script>
import { BCard, BButton, BFormSelect, BOverlay } from "bootstrap-vue";
import { VueNestable, VueNestableHandle } from "vue-nestable";
import WebsiteApi from "@/api/website";
import NavigationForm from "./components/NavigationForm.vue";

export default {
    name: "NavigationManagement",
    components: {
        BCard,
        BButton,
        BFormSelect,
        BOverlay,
        VueNestable,
        NavigationForm,
        VueNestableHandle,
    },
    data() {
        return {
            loading: false,
            treeLoading: false,
            editing: false,
            errors: {},
            selectedLanguage: "tr",
            selectedNavigationType: "main",
            languages: [],
            navigationTree: [],
            contents: [],
            navigationTypes: [
                { label: "Ana Menü", value: "main" },
                { label: "Footer Menü", value: "footer" },
                { label: "Yan Menü", value: "sidebar" },
            ],
            editedItem: {
                id: null,
                parentNavigationId: null,
                navigationKey: "",
                navigationType: "main",
                contentIds: [],
                customUrl: "",
                isActive: true,
                openInNewTab: false,
                showCta: false,
                ctaText: "",
                ctaHref: "",
                sortOrder: 0,
                translations: [],
            },
            suppressOrderUpdate: false,
            savingOrder: false,
        };
    },
    computed: {
        formattedParentNavigations() {
            return this.flattenNavigationsForSelect(this.navigationTree);
        },
        formattedContents() {
            if (!this.contents || !Array.isArray(this.contents)) return [];
            return this.contents.map((item) => ({
                Id: item.Id,
                Title: (item.Translations && item.Translations[0] ? item.Translations[0].Title : null) || item.ContentKey || "Başlıksız",
            }));
        },
    },
    created() {
        this.loadLanguages();
        this.loadNavigations();
        this.loadContents();
    },
    watch: {
        selectedLanguage(newLang) {
            this.loadNavigations();
            this.loadContents();
        },
        navigationTree: {
            deep: true,
            handler(newValue) {
                if (this.suppressOrderUpdate || this.savingOrder) return;

                const items = Array.isArray(newValue) ? newValue : [];
                const flattenedOrder = this.flattenTreeForOrder(items);

                if (!flattenedOrder.length) return;

                this.savingOrder = true;

                WebsiteApi.Navigation.UpdateOrder(flattenedOrder)
                    .then((response) => {
                        this.$result(response);
                    })
                    .catch((err) => {
                        this.$result(null, err);
                        this.loadNavigations();
                    })
                    .finally(() => {
                        this.savingOrder = false;
                    });
            },
        },
    },
    methods: {
        async loadLanguages() {
            const response = await WebsiteApi.Language.GetAll();
            if (response.data.Success && response.data.Data) {
                this.languages = response.data.Data;
            }
        },

        loadContents() {
            WebsiteApi.Content.GetContents({
                LanguageCode: this.selectedLanguage,
                IncludeTranslations: true,
            })
                .then((response) => {
                    if (response.data.Success && response.data.Data) {
                        this.contents = response.data.Data;
                    }
                })
                .catch((error) => {
                    console.error("İçerikler yüklenemedi:", error);
                    this.$result(null, error);
                });
        },

        async loadNavigations() {
            this.treeLoading = true;
            this.suppressOrderUpdate = true;
            try {
                const response = await WebsiteApi.Navigation.GetTree(this.selectedNavigationType, this.selectedLanguage);
                if (response.data.Success && response.data.Data) {
                    this.navigationTree = this.transformForNestable(response.data.Data);
                }
            } catch (error) {
                this.$result(null, error);
            } finally {
                this.treeLoading = false;
                this.$nextTick(() => {
                    this.suppressOrderUpdate = false;
                });
            }
        },

        transformForNestable(items) {
            if (!items || !Array.isArray(items)) return [];
            return items.map((item) => ({
                id: item.Id, // vue-nestable’ın kendi key’i
                Id: item.Id, // backend için
                ParentNavigationId: item.ParentNavigationId,
                NavigationKey: item.NavigationKey,
                NavigationType: item.NavigationType,
                ContentId: item.ContentId,
                CustomUrl: item.CustomUrl,
                IsActive: item.IsActive,
                OpenInNewTab: item.OpenInNewTab,
                ShowCta: item.ShowCta,
                SortOrder: item.SortOrder,
                Translations: item.Translations,
                children: this.transformForNestable(item.ChildNavigations || []),
            }));
        },

        flattenNavigationsForSelect(items, result = []) {
            if (!items || !Array.isArray(items)) return result;
            items.forEach((item) => {
                result.push({
                    Id: item.Id,
                    Title: this.getTranslation(item, this.selectedLanguage),
                    Translations: item.Translations,
                });
                if (item.children && item.children.length > 0) {
                    this.flattenNavigationsForSelect(item.children, result);
                }
            });
            return result;
        },

        getTranslation(item, languageCode) {
            if (!item.Translations || !Array.isArray(item.Translations)) {
                return item.NavigationKey || "Başlıksız";
            }
            const translation = item.Translations.find((t) => t.LanguageCode === languageCode);
            return translation?.Title || item.NavigationKey || "Başlıksız";
        },

        changeNavigationType(value) {
            this.selectedNavigationType = value;
            this.loadNavigations();
        },

        clearForm() {
            this.editing = false;
            this.errors = {};
            this.editedItem = {
                id: null,
                parentNavigationId: null,
                navigationKey: "",
                navigationType: this.selectedNavigationType,
                contentIds: [],
                customUrl: "",
                isActive: true,
                openInNewTab: false,
                showCta: false,
                ctaText: "",
                ctaHref: "",
                sortOrder: 0,
                translations: [],
            };
        },

        createNew() {
            this.clearForm();
            this.$bvModal.show("modal-navigation");
        },

        edit(item) {
            this.clearForm();
            this.editing = true;

            WebsiteApi.Navigation.GetById(item.Id, this.selectedLanguage)
                .then((response) => {
                    if (response.data.Success && response.data.Data) {
                        const data = response.data.Data;

                        // Transform contents array to contentIds format
                        let contentIds = [];
                        if (data.Contents && Array.isArray(data.Contents)) {
                            contentIds = data.Contents.map((content) => ({
                                contentId: content.ContentId,
                                isPrimary: content.IsPrimary || false,
                                sortOrder: content.SortOrder || 0,
                                isActive: content.IsActive !== undefined ? content.IsActive : true,
                            }));
                        }
                        // Fallback for backward compatibility with old contentId field
                        else if (data.ContentId) {
                            contentIds = [
                                {
                                    contentId: data.ContentId,
                                    isPrimary: true,
                                    sortOrder: 0,
                                    isActive: true,
                                },
                            ];
                        }

                        this.editedItem = {
                            id: data.Id,
                            parentNavigationId: data.ParentNavigationId,
                            navigationKey: data.NavigationKey,
                            navigationType: data.NavigationType,
                            contentIds: contentIds,
                            customUrl: data.CustomUrl,
                            isActive: data.IsActive,
                            openInNewTab: data.OpenInNewTab,
                            showCta: data.ShowCta,
                            sortOrder: data.SortOrder,
                            translations: data.Translations || [],
                        };
                        this.$bvModal.show("modal-navigation");
                    }
                })
                .catch((error) => {
                    this.$result(null, error);
                });
        },

        save() {
            this.errors = {};

            if (!this.editedItem.navigationKey) {
                this.$set(this.errors, "navigationKey", "Navigasyon anahtarı zorunludur.");
                return;
            }

            // Validate content IDs
            if (this.editedItem.contentIds && this.editedItem.contentIds.length > 0) {
                const contentIdSet = new Set();
                let primaryCount = 0;

                for (const item of this.editedItem.contentIds) {
                    // Check duplicates
                    if (contentIdSet.has(item.contentId)) {
                        this.$set(this.errors, "contentIds", "Aynı içerik birden fazla kez eklenemez!");
                        return;
                    }
                    contentIdSet.add(item.contentId);

                    // Count primary contents
                    if (item.isPrimary) {
                        primaryCount++;
                    }
                }

                // Check multiple primary
                if (primaryCount > 1) {
                    this.$set(this.errors, "contentIds", "Bir navigasyonda yalnızca bir ana içerik olabilir!");
                    return;
                }
            }

            // Transform contentIds for backend
            const contentIds = this.editedItem.contentIds && this.editedItem.contentIds.length > 0 ? this.editedItem.contentIds.map((c) => ({
                ContentId: c.contentId,
                IsPrimary: c.isPrimary || false,
                SortOrder: c.sortOrder || 0,
                IsActive: c.isActive !== undefined ? c.isActive : true,
            })) : [];

            const payload = {
                ParentNavigationId: this.editedItem.parentNavigationId,
                NavigationKey: this.editedItem.navigationKey,
                NavigationType: this.editedItem.navigationType,
                ContentIds: contentIds,
                CustomUrl: this.editedItem.customUrl,
                IsActive: this.editedItem.isActive,
                OpenInNewTab: this.editedItem.openInNewTab,
                ShowCta: this.editedItem.showCta,
                SortOrder: this.editedItem.sortOrder,
                Translations: this.editedItem.translations,
            };

            this.loading = true;

            const apiCall = this.editing ? WebsiteApi.Navigation.Update(this.editedItem.id, payload) : WebsiteApi.Navigation.Create(payload);

            apiCall
                .then((response) => {
                    if (response.data.Success) {
                        this.loadNavigations();
                        this.$bvModal.hide("modal-navigation");
                    } else if (response.data && response.data.errors) {
                        this.errors = response.data.errors;
                    }
                    this.$result(response);
                })
                .catch((err) => {
                    if (err.response && err.response.data && err.response.data.errors) {
                        this.errors = err.response.data.errors;
                    }
                    this.$result(null, err);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        deleteItem(item) {
            this.$confirm("Navigasyonu Sil", `"${this.getTranslation(item, this.selectedLanguage)}" navigasyonunu silmek istediğinize emin misiniz? Alt menüler de silinecektir.`).then((result) => {
                if (result) {
                    WebsiteApi.Navigation.Delete(item.Id)
                        .then((response) => {
                            if (response.data.Success) {
                                this.loadNavigations();
                            }
                            this.$result(response);
                        })
                        .catch((err) => {
                            this.$result(null, err);
                        });
                }
            });
        },

        handleOrderChange(value, options) {
            const items = (options && options.items) || value || [];

            const flattenedOrder = this.flattenTreeForOrder(items);

            if (!flattenedOrder.length) {
                return;
            }

            WebsiteApi.Navigation.UpdateOrder(flattenedOrder)
                .then((response) => {
                    this.$result(response);
                    if (response.data && response.data.Success) {
                        this.loadNavigations();
                    }
                })
                .catch((err) => {
                    this.$result(null, err);
                    this.loadNavigations();
                });
        },

        flattenTreeForOrder(items, parentId = null, result = []) {
            if (!items || !Array.isArray(items)) {
                return result;
            }

            items.forEach((item, index) => {
                result.push({
                    Id: item.Id,
                    ParentNavigationId: parentId,
                    SortOrder: index,
                });

                if (item.children && item.children.length > 0) {
                    this.flattenTreeForOrder(item.children, item.Id, result);
                }
            });

            return result;
        },
    },
};
</script>

<style lang="scss" scoped>
.navigation-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e9ecef;
}

.nav-type-selector {
    display: flex;
    gap: 8px;
    background: #f8f9fa;
    padding: 4px;
    border-radius: 8px;
}

.nav-type-btn {
    padding: 8px 20px;
    border: none;
    background: transparent;
    color: #6c757d;
    font-size: 14px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: #fff;
        color: #495057;
    }

    &.active {
        background: #fff;
        color: #7367f0;
        box-shadow: 0 2px 4px rgba(115, 103, 240, 0.15);
    }
}

.navigation-controls {
    display: flex;
    align-items: center;
    gap: 12px;
}

.language-select {
    width: 140px;
}

.navigation-list {
    background: #fff;
}

.nav-item {
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
}

.nav-item-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
        border-color: #d1d5db;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    }
}

.nav-item-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.drag-handle {
    color: #9ca3af;
    cursor: grab !important;
    flex-shrink: 0;

    &:active {
        cursor: grabbing !important;
    }

    &:hover {
        color: #6b7280;
    }
}

::v-deep .nestable-drag-layer > .nestable-list {
    pointer-events: none;
}

::v-deep .nestable-item.is-dragging {
    opacity: 0.5;
}

.nav-item-title {
    font-weight: 500;
    color: #1f2937;
    font-size: 14px;
}

.nav-item-key {
    padding: 2px 8px;
    background: #fef3c7;
    color: #92400e;
    font-size: 12px;
    border-radius: 4px;
    font-weight: 500;
}

.nav-item-right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.nav-item-status {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;

    &.active {
        background: #d1fae5;
        color: #065f46;
    }

    &.inactive {
        background: #fee2e2;
        color: #991b1b;
    }
}

.nav-action-btn {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &.edit {
        color: #6b7280;

        &:hover {
            background: #f3f4f6;
            color: #7367f0;
        }
    }

    &.delete {
        color: #6b7280;

        &:hover {
            background: #fef2f2;
            color: #ef4444;
        }
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
}

.empty-icon {
    color: #d1d5db;
    margin-bottom: 16px;
}

.empty-text {
    color: #9ca3af;
    font-size: 14px;
    margin-bottom: 24px;
}

::v-deep .nestable-list {
    padding-left: 40px;
}

::v-deep .nestable-item {
    margin-bottom: 0;
}

::v-deep .nestable-item-content {
    padding: 0;
    background: transparent;
    border: none;
}

.dark-layout .nav-type-selector {
    background-color: #161d31;
    border: 1px solid #3b4253;
}
.dark-layout .nav-type-btn {
    color: #b4b7bd;
}
.dark-layout .nav-type-btn:hover {
    background-color: #283046;
    color: #d0d2d6;
}
.dark-layout .nav-type-btn.active {
    background-color: #7367f0 !important;
    color: #fff !important;
    box-shadow: 0 4px 8px rgba(115, 103, 240, 0.4);
}

.dark-layout .navigation-list {
    background-color: #283046;
    border-radius: 6px;
}

.dark-layout .nav-item-content {
    background-color: #161d31;
    border-color: #3b4253;
}
.dark-layout .nav-item-content:hover {
    border-color: #7367f0;
}
.dark-layout .nav-item-title {
    color: #d0d2d6;
}
.dark-layout .drag-handle {
    color: #676d7d;
}

.dark-layout .nav-item-key {
    background-color: #3b4253;
    color: #ff9f43;
}

.dark-layout .nav-action-btn.edit:hover {
    background-color: #3b4253;
    color: #7367f0;
}
.dark-layout .nav-action-btn.delete:hover {
    background-color: #3b4253;
    color: #ea5455;
}
</style>
